/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Component, OnInit } from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { StringService } from 'string-service';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import {
    IAviDiffGridConfig,
} from 'ng/modules/data-grid/components/avi-diff-grid/avi-diff-grid.types';

import * as l10n from './log-cinematic-sub-section-o-auth.l10n';
import { TrafficLogCinematicStore } from '../../../traffic-logs/traffic-log-cinematic/traffic-log-cinematic.store';

import './log-cinematic-sub-section-o-auth.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

const oauthSubRequestLogNames = ['jwks_subrequest', 'token_exchange_subrequest',
    'token_introspection_subrequest', 'token_refresh_subrequest', 'userinfo_subrequest'];

const oauthSubRequestLogFilterableProperties =
    ['pool_name', 'source_port', 'total_time', 'error_code', 'server_name', 'server_ip',
    'server_port', 'http_response_code'];

/** Field info for each sub-reqeust log under an oAuth Log */
interface IOAuthSubRequestLogField {
    fieldName: string;
    fieldLabel: string;
}

type TSubRequestLogFilterPropertyHash = Record<string, Record<string, string>>;

/**
 * @description
 *     Component for displaying the OAuth sub-section in the Authentication section under traffic log cinematic view.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'log-cinematic-sub-section-o-auth',
    templateUrl: './log-cinematic-sub-section-o-auth.component.html',
})
export class LogCinematicSubSectionOAuthComponent implements OnInit {
    public readonly oAuthLog$ = this.logCinematicStore.oAuthLog$;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /**
     * List of info containing field names and field labels for oAuth sub-request logs.
     */
    public oAuthSubRequestFields: IOAuthSubRequestLogField[];

    /**
     * Map from filterable property names of OAuth SubRequestLogs to the full string of chained
     * filter property names from the top level.
     * Eg. subRequestLogFilterPropertyHash['jwks_subrequest']['server_port'] yields the resulted
     * string as 'oauth_log.jwks_subrequest.sub_request_log.server_port'.
     */
    public subRequestLogFilterPropertyHash: TSubRequestLogFilterPropertyHash = {};

    /**
     * Config for the diff grid showing OAuth "sent to server" and "received from server" headers.
     */
    public headersDiffGridConfig: IAviDiffGridConfig;

    constructor(
        public readonly logCinematicStore: TrafficLogCinematicStore,
        private readonly l10nService: L10nService,
        private readonly stringService: StringService,
        public readonly schemaService: SchemaService,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    public trackByIndex(index: number): number {
        return index;
    }

    /** @override */
    public ngOnInit(): void {
        this.setOAuthSubRequestFields();
        this.setSubRequestLogFilterPropertyHash();

        this.headersDiffGridConfig = {
            leftField: {
                label: this.l10nService.getMessage(globalL10nKeys.sentToServerLabel),
            },
            rightField: {
                label: this.l10nService.getMessage(globalL10nKeys.receivedFromServerLabel),
            },
            layout: {
                placeholderMessage:
                    this.l10nService.getMessage(globalL10nKeys.noHttpHeadersToShowPlaceholder),
            },
            isGridExpandable: true,
            noDiff: true,
        };
    }

    /**
     * Set a list of the named sub-request logs under OAuth log with their names and labels.
     */
    private setOAuthSubRequestFields(): void {
        this.oAuthSubRequestFields = oauthSubRequestLogNames.map(
            fieldName => {
                const fieldCamelCaseName = this.stringService.snakeToCamelCase(fieldName);
                const l10nKey = `${fieldCamelCaseName}Label`;
                const fieldLabel = this.l10nService.getMessage(l10nKeys[l10nKey]);

                return {
                    fieldName,
                    fieldLabel,
                };
            },
        );
    }

    /**
     * Set a map from filterable property names of OAuth SubRequestLogs to the full string of
     * chained filter property names from the top level. This hash is nested with the sub request
     * log key to first access the filter property string hash of that subReqLog and the property
     * key to get the actualy property string.
     * Eg. subRequestLogFilterPropertyHash['jwks_subrequest']['server_port'] yields the resulted
     * string as 'oauth_log.jwks_subrequest.sub_request_log.server_port'.
     */
    private setSubRequestLogFilterPropertyHash(): void {
        oauthSubRequestLogNames.forEach(subReqLogName => {
            const subReqLogPropertyHash = {};

            oauthSubRequestLogFilterableProperties.forEach(propertyName => {
                let propertyString: string;

                if (propertyName !== 'error_code') {
                    propertyString = `oauth_log.${subReqLogName}.sub_request_log.${propertyName}`;
                } else {
                    propertyString = `oauth_log.${subReqLogName}.${propertyName}`;
                }

                subReqLogPropertyHash[propertyName] = propertyString;
            });

            this.subRequestLogFilterPropertyHash[subReqLogName] = subReqLogPropertyHash;
        });
    }
}
